import { useCallback, useMemo, useRef, useState } from 'react';
import type { BookableObject, EventManager, HoldToken, SelectableObject } from '@seatsio/seatsio-types';

import type { SelectionByCategoryEntry } from 'features/common/components/Punchup/primitives/display/PunchupSeatsCategoryBreakdown/types';

export type { SelectionByCategoryEntry };

export type SeatsSelectionMode = 'select' | 'manageForSaleConfig';

export interface SeatsSelectionChartProps {
	onChartRendered: (chart: EventManager) => void;
	onObjectSelected?: (object: SelectableObject) => void;
	onObjectDeselected?: (object: SelectableObject) => void;
	onObjectStatusChanged?: (object: BookableObject) => void;
	onSessionInitialized?: (holdToken: HoldToken) => void;
}

export interface UseSeatsSelectionResult {
	/**
	 * `select` mode: the hold token of the chart's current seats.io session ('' before one starts). An order
	 * books the selection under it — a whole GA area can only be booked under the hold that holds it.
	 */
	holdToken: string;
	/** Flat list of selected (or off-sale) labels, one per place — a general admission area repeats its label. */
	selection: string[];
	/** Same selection grouped by seats.io category key. */
	selectionByCategory: SelectionByCategoryEntry[];
	/** Spread these onto SeatsioEventManager — they wire the relevant SDK callbacks. */
	chartProps: SeatsSelectionChartProps;
	/** Clears local selection state. Useful after a successful save. */
	reset: () => void;
	/**
	 * Programmatically remove `label` from the chart and from this hook's local state.
	 *
	 * - **`select` mode:** calls `chart.deselectObjects([label])` — documented SDK
	 *   behaviour, reliably reverts the selection.
	 * - **`manageForSaleConfig` mode:** the same call *attempts* to revert the for-sale
	 *   toggle. The SDK does not document this behaviour explicitly, but the
	 *   `EventManager` interface inherits `deselectObjects` from `SeatingChart` so the
	 *   call is type-valid. If the live widget does not honour it, the local entries map
	 *   is still cleared so the state machine in the consuming form remains consistent
	 *   (the seat will still appear off-sale on the chart, but our save-side count drops).
	 *
	 * Errors thrown by the chart are swallowed (logged via console.warn) so callers can
	 * fire-and-forget. The returned promise resolves regardless of SDK outcome.
	 */
	deselectObject: (label: string) => Promise<void>;
}

interface CategoryEntry {
	categoryKey: string;
	categoryLabel: string;
	color: string;
}

interface ObjectEntry extends CategoryEntry {
	count: number;
}

const isOffSaleStatus = (object: BookableObject): boolean => {
	// Custom statuses are stringly typed; the for-sale flag is the canonical field but some
	// SDK builds only flip the `status` literal — handle both.
	if (typeof object.forSale === 'boolean') return object.forSale === false;
	return object.status === 'not_for_sale';
};

const extractCategory = (object: SelectableObject | BookableObject): CategoryEntry | null => {
	const cat = (object as { category?: { key?: string | number; label?: string; color?: string } }).category;
	if (!cat || cat.key === undefined || cat.key === null) return null;
	return {
		categoryKey: String(cat.key),
		categoryLabel: cat.label ?? String(cat.key),
		color: cat.color ?? '#cccccc',
	};
};

const getObjectLabel = (object: SelectableObject | BookableObject): string | null => {
	const label = (object as { label?: string }).label;
	return label ? String(label) : null;
};

const numSelectedOf = (object: SelectableObject): number => {
	const numSelected = (object as { numSelected?: number }).numSelected;
	return typeof numSelected === 'number' ? numSelected : 0;
};

// seats.io lists a general admission area once per place or once with numSelected — take the larger.
const entriesFromChart = (objects: SelectableObject[]): Record<string, ObjectEntry> => {
	const entries: Record<string, ObjectEntry> = {};
	const occurrences: Record<string, number> = {};
	for (const object of objects) {
		const label = getObjectLabel(object);
		const category = extractCategory(object);
		if (!label || !category) continue;
		occurrences[label] = (occurrences[label] ?? 0) + 1;
		entries[label] = { ...category, count: Math.max(occurrences[label], numSelectedOf(object)) };
	}
	return entries;
};

/**
 * useSeatsSelection
 *
 * Single contact point with the seats.io React SDK for our forms. Encapsulates
 * `onChartRendered`, `onObjectSelected`, `onObjectDeselected`, and `onObjectStatusChanged`
 * so callers (`PoolForm`, `PunchupSendTickets`, etc.) never bind callbacks themselves.
 *
 * Returns the current selection plus a per-category breakdown that the form uses to
 * derive `total_amount` and resolve the bound ticket type.
 *
 * Modes:
 * - `'select'` — on every select/deselect callback, re-reads the chart's selection with
 *   `listSelectedObjects()` (used by Send Tickets / Comp and the Box Office), and keeps the
 *   session's `holdToken` (`onSessionInitialized`).
 * - `'manageForSaleConfig'` — tracks the off-sale set during this form session via
 *   `onObjectStatusChanged` (used by Pool form). Initial off-sale state from previous
 *   sessions is intentionally NOT seeded; per-pool seat persistence is deferred backend
 *   work (D-1 in `seats_business_logic.md`). The pool's `total_amount` therefore represents
 *   the seats this session has flipped off-sale, which is the documented limitation.
 *
 * @param eventKey  seats.io event key — currently used only as a memo guard so the chart
 *                  callbacks reset when the event changes.
 * @param mode      Which set of callbacks the consumer wants wired.
 */
export function useSeatsSelection(
	eventKey: string | null | undefined,
	mode: SeatsSelectionMode
): UseSeatsSelectionResult {
	const [entries, setEntries] = useState<Record<string, ObjectEntry>>({});
	const [holdToken, setHoldToken] = useState('');
	const chartRef = useRef<EventManager | null>(null);
	// Bumped on every chart read and on reset, so only the latest read may write the selection.
	const syncSeqRef = useRef(0);

	const syncFromChart = useCallback(async () => {
		const chart = chartRef.current;
		if (!chart) return;
		const seq = ++syncSeqRef.current;
		try {
			const objects = await chart.listSelectedObjects();
			if (seq === syncSeqRef.current && chart === chartRef.current) setEntries(entriesFromChart(objects));
		} catch (err) {
			// Fail closed: a stale selection would price and book the wrong seats.
			if (seq === syncSeqRef.current) setEntries({});
			// eslint-disable-next-line no-console
			console.warn('[useSeatsSelection] listSelectedObjects failed', err);
		}
	}, []);

	const reset = useCallback(() => {
		syncSeqRef.current += 1;
		setEntries({});
		// Also clear the chart's own selection — clearing only local state left the previously-picked
		// seats highlighted on a same-event mode flip (e.g. toggling "Only move seats"). clearSelection,
		// not deselectObjects(labels): deselecting a general admission area's label drops one place.
		chartRef.current?.clearSelection().catch((err) => {
			// eslint-disable-next-line no-console
			console.warn('[useSeatsSelection] reset clearSelection failed', err);
		});
	}, []);

	const deselectObject = useCallback(async (label: string) => {
		// Always clear our local state first so the consuming form's state machine reflects
		// "this label is not in the user's pool" regardless of whether the SDK call below
		// succeeds. If the SDK reverts the chart too, the next onObjectStatusChanged for
		// this label will be a no-op (label already absent from entries).
		setEntries((prev) => {
			if (!(label in prev)) return prev;
			const next = { ...prev };
			delete next[label];
			return next;
		});
		const chart = chartRef.current;
		if (!chart) return;
		try {
			await chart.deselectObjects([label]);
		} catch (err) {
			// Swallow — local state is already consistent. Logged for debugging.
			// eslint-disable-next-line no-console
			console.warn('[useSeatsSelection] chart.deselectObjects failed for', label, err);
		}
	}, []);

	const onChartRendered = useCallback(
		(chart: EventManager) => {
			const replaced = chartRef.current !== null && chartRef.current !== chart;
			chartRef.current = chart;
			// seatsio-react rebuilds the chart when a prop changes (a channel switch, new pricing), and with
			// session="start" the rebuilt chart opens a new hold with nothing selected. Re-read it, so an
			// order never carries places the new chart and its hold token do not hold.
			if (replaced && mode === 'select') void syncFromChart();
		},
		[mode, syncFromChart]
	);

	const onSelectionChanged = useCallback(() => {
		void syncFromChart();
	}, [syncFromChart]);

	// A new session holds nothing yet: drop the old selection in the same update that swaps the token, and
	// void any read still in flight, so an order can never pair the old places with the new hold.
	const onSessionInitialized = useCallback((token: HoldToken) => {
		syncSeqRef.current += 1;
		setEntries({});
		setHoldToken(token.token);
	}, []);

	const onObjectStatusChanged = useCallback((object: BookableObject) => {
		const label = getObjectLabel(object);
		const entry = extractCategory(object);
		if (!label) return;
		const offSale = isOffSaleStatus(object);
		setEntries((prev) => {
			const isTracked = label in prev;
			if (offSale) {
				if (!entry) return prev;
				if (isTracked && prev[label].categoryKey === entry.categoryKey) return prev;
				return { ...prev, [label]: { ...entry, count: 1 } };
			}
			if (!isTracked) return prev;
			const next = { ...prev };
			delete next[label];
			return next;
		});
	}, []);

	const chartProps = useMemo<SeatsSelectionChartProps>(() => {
		// eventKey is referenced so a new key gives the consumer a fresh callback identity —
		// helpful when the SeatsioEventManager remounts on event change.
		void eventKey;
		if (mode === 'select') {
			return {
				onChartRendered,
				onObjectSelected: onSelectionChanged,
				onObjectDeselected: onSelectionChanged,
				onSessionInitialized,
			};
		}
		return { onChartRendered, onObjectStatusChanged };
	}, [eventKey, mode, onChartRendered, onSelectionChanged, onSessionInitialized, onObjectStatusChanged]);

	const { selection, selectionByCategory } = useMemo(() => {
		const labels: string[] = [];
		const grouped = new Map<string, SelectionByCategoryEntry>();
		for (const [label, e] of Object.entries(entries)) {
			const places = Array<string>(e.count).fill(label);
			labels.push(...places);
			const existing = grouped.get(e.categoryKey);
			if (existing) {
				existing.objectLabels.push(...places);
			} else {
				grouped.set(e.categoryKey, {
					categoryKey: e.categoryKey,
					categoryLabel: e.categoryLabel,
					color: e.color,
					objectLabels: places,
				});
			}
		}
		const groups = Array.from(grouped.values()).map((g) => ({
			...g,
			objectLabels: [...g.objectLabels].sort(),
		}));
		groups.sort((a, b) => a.categoryLabel.localeCompare(b.categoryLabel));
		return { selection: labels.sort(), selectionByCategory: groups };
	}, [entries]);

	return { selection, selectionByCategory, holdToken, chartProps, reset, deselectObject };
}
